import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { TypeBadge, StatusBadge, MhdBadge } from "../components/Badges";
import { ArrowLeft, Package, Layers, AlertTriangle, CheckCircle2, Factory, Image as ImageIcon, History, TrendingUp, TrendingDown, Move, PackagePlus } from "lucide-react";
import { toast } from "sonner";

export default function ItemDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [costing, setCosting] = useState(null);

  useEffect(() => {
    api.get(`/items/${id}/detail`)
      .then((r) => {
        setData(r.data);
        if (r.data.type === "finished") {
          api.get(`/items/${id}/costing`).then((c) => setCosting(c.data)).catch(() => {});
        }
      })
      .catch((err) => { toast.error(err.response?.data?.detail || "Artikel nicht gefunden"); nav("/artikel"); })
      .finally(() => setLoading(false));
  }, [id, nav]);

  if (loading) return <div className="p-6 text-[var(--text-muted)]">Lade…</div>;
  if (!data) return null;

  const m = data.mhd_stats || {};
  return (
    <div data-testid="page-item-detail">
      <div className="mb-4">
        <Link to="/artikel" data-testid="btn-back-items"
              className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--pop-black)]">
          <ArrowLeft size={14} /> Zurück zur Übersicht
        </Link>
      </div>

      <div className="flex items-start gap-6 mb-6">
        <div className="w-40 h-40 rounded-2xl border border-[var(--border)] bg-[var(--pop-cream)]/30 flex items-center justify-center overflow-hidden shrink-0" data-testid="item-image">
          {data.image_data
            ? <img src={data.image_data} alt={data.name} className="w-full h-full object-cover" />
            : <ImageIcon size={40} className="text-[var(--text-muted)]" />}
        </div>
        <div className="flex-1">
          <div className="text-xs text-[var(--pop-pink)] uppercase tracking-[2px] font-bold mb-1">Artikel</div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black text-4xl">{data.name}</h1>
            <TypeBadge type={data.type} />
          </div>
          <div className="font-mono text-sm text-[var(--text-muted)] mt-1">
            SKU: {data.sku}{data.barcode ? ` · Barcode: ${data.barcode}` : ""}
          </div>
          {data.description && <div className="text-sm mt-2 max-w-2xl">{data.description}</div>}
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => nav(`/artikel?edit=${data.id}`)}
                    className="rounded-full" data-testid="btn-edit-item-detail">Bearbeiten</Button>
            <Button variant="outline" onClick={() => openPdf(`/items/${data.id}/barcode.pdf`).catch(() => toast.error("Barcode konnte nicht geöffnet werden"))}
                    className="rounded-full" data-testid="btn-barcode-item-detail">Barcode drucken</Button>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card className="p-4 rounded-2xl">
          <div className="overline">Aktueller Bestand</div>
          <div className="font-display font-black text-3xl mt-1" data-testid="kpi-stock">
            {Number(data.stock || 0).toLocaleString("de-DE")} <span className="text-sm text-[var(--text-muted)]">{data.unit}</span>
          </div>
        </Card>
        <Card className="p-4 rounded-2xl">
          <div className="overline">Min-Bestand</div>
          <div className="font-display font-black text-3xl mt-1">{Number(data.min_stock || 0).toLocaleString("de-DE")}</div>
        </Card>
        <Card className="p-4 rounded-2xl">
          <div className="overline">Preis</div>
          <div className="font-display font-black text-3xl mt-1">
            {data.price != null ? `${Number(data.price).toFixed(2)} €` : "—"}
          </div>
        </Card>
        <Card className="p-4 rounded-2xl">
          <div className="overline">Regalplatz</div>
          <div className="font-mono font-bold text-2xl mt-1">{data.warehouse_location || "—"}</div>
        </Card>
        <Card className={`p-4 rounded-2xl ${m.expired > 0 ? "bg-red-50 border-red-300" : m.warning_30d > 0 ? "bg-yellow-50 border-yellow-300" : ""}`}>
          <div className="overline flex items-center gap-1">
            {m.expired > 0 ? <AlertTriangle size={12} className="text-red-700" /> : <CheckCircle2 size={12} className="text-green-700" />}
            MHD-Status
          </div>
          <div className="font-display font-black text-2xl mt-1" data-testid="kpi-mhd">
            {m.expired > 0
              ? <span className="text-red-700">{m.expired} abgelaufen</span>
              : m.warning_30d > 0
                ? <span className="text-yellow-800">{m.warning_30d} &lt; 30T</span>
                : <span className="text-green-700">{m.ok} ok</span>}
          </div>
          <div className="text-[10px] font-mono text-[var(--text-muted)] mt-1">
            {m.total_batches} aktive Chargen
          </div>
        </Card>
      </div>

      {/* Kalkulation für Fertigwaren */}
      {costing && (
        <Card className="p-4 rounded-2xl mb-6" data-testid="costing-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-[var(--pop-pink)]" />
            <div className="font-display font-bold text-lg">Kalkulation & Marge</div>
            {costing.recipe_name && (
              <span className="text-xs text-[var(--text-muted)] ml-2">
                aus Rezept „{costing.recipe_name}" · {costing.output_quantity} {costing.output_unit || ""}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <StatBox label="Rohstoffkosten" value={`${costing.raw_cost.toFixed(2)} €`} />
            <StatBox label="Produktionskosten" value={`${costing.production_cost.toFixed(2)} €`} sub="je Charge" />
            <StatBox label="Kosten je Einheit" value={`${costing.cost_per_unit.toFixed(2)} €`} highlight />
            <StatBox label="VK-Preis" value={costing.price ? `${costing.price.toFixed(2)} €` : "—"} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <StatBox label="Marge €" value={costing.margin_eur ? `${costing.margin_eur.toFixed(2)} €` : "—"}
                      tone={costing.margin_eur > 0 ? "green" : costing.margin_eur < 0 ? "red" : ""} />
            <StatBox label="Marge %" value={costing.margin_pct ? `${costing.margin_pct.toFixed(1)} %` : "—"}
                      tone={costing.margin_pct >= (costing.target_margin_pct || 40) ? "green" : costing.margin_pct < 0 ? "red" : "yellow"} />
            <StatBox label={`Empfohlener VK (Ziel ${costing.target_margin_pct}%)`}
                      value={costing.suggested_price ? `${costing.suggested_price.toFixed(2)} €` : "—"} highlight />
          </div>
          {costing.ingredients && costing.ingredients.length > 0 && (
            <details className="mt-2 rounded-xl border border-[var(--border)] p-2">
              <summary className="font-bold text-xs cursor-pointer">Zutaten-Breakdown ({costing.ingredients.length})</summary>
              <Table className="table-zebra mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead className="overline">Zutat</TableHead>
                    <TableHead className="overline text-right">Menge</TableHead>
                    <TableHead className="overline text-right">EK Preis</TableHead>
                    <TableHead className="overline text-right">Kosten</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costing.ingredients.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{l.name} <span className="text-xs font-mono text-[var(--text-muted)]">{l.sku}</span></TableCell>
                      <TableCell className="text-right font-mono text-xs">{l.quantity} {l.unit}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{l.purchase_price ? `${l.purchase_price.toFixed(2)} €/${l.purchase_unit}` : "—"}</TableCell>
                      <TableCell className="text-right font-mono font-bold">{l.cost.toFixed(2)} €</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </details>
          )}
        </Card>
      )}

      {/* Chargen */}
      <Card className="p-0 overflow-hidden rounded-2xl mb-6">
        <div className="p-3 border-b flex items-center gap-2">
          <Layers size={16} className="text-[var(--pop-pink)]" />
          <div className="font-display font-bold text-lg">Aktive Chargen (FEFO)</div>
          <span className="ml-auto text-xs text-[var(--text-muted)]">{data.batches.length} Chargen</span>
        </div>
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Charge-Nr.</TableHead>
              <TableHead className="overline">MHD</TableHead>
              <TableHead className="overline text-right">Bestand</TableHead>
              <TableHead className="overline">Regal</TableHead>
              <TableHead className="overline">QC</TableHead>
              <TableHead className="overline text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.batches.map((b) => {
              const isExpired = b.days_to_mhd != null && b.days_to_mhd < 0;
              return (
                <TableRow key={b.id} data-testid={`detail-batch-${b.charge_number}`}
                          className={isExpired ? "bg-red-50" : ""}>
                  <TableCell className="font-mono text-xs">
                    {b.charge_number}
                    {b.container_count && b.container_size && (
                      <div className="text-[10px] text-orange-700">
                        {b.container_count} {b.container_label || "Gebinde"} à {b.container_size} {b.container_unit || "kg"}
                        {(b.container_unit === "l" || b.container_unit === "ml") && (
                          <span className="ml-1 italic text-blue-700">· Flüssigkeit</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {b.mhd} <MhdBadge days={b.days_to_mhd} />
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold">
                    {b.current_stock} <span className="text-xs text-[var(--text-muted)]">{data.unit}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{b.location || "—"}</TableCell>
                  <TableCell><StatusBadge status={b.quality_status} /></TableCell>
                  <TableCell className="text-right">
                    {isExpired
                      ? <span className="pop-pill bg-red-600 text-white font-bold text-[10px]">abgelaufen</span>
                      : b.days_to_mhd != null && b.days_to_mhd <= 30
                        ? <span className="pop-pill bg-orange-100 text-orange-800 font-bold text-[10px]">bald ablaufend</span>
                        : <span className="text-xs text-[var(--text-muted)]">ok</span>}
                  </TableCell>
                </TableRow>
              );
            })}
            {data.batches.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[var(--text-muted)]">Keine aktiven Chargen.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Produktionsaufträge */}
      <Card className="p-0 overflow-hidden rounded-2xl mb-6">
        <div className="p-3 border-b flex items-center gap-2">
          <Factory size={16} className="text-[var(--pop-caramel)]" />
          <div className="font-display font-bold text-lg">Zugehörige Produktionsaufträge</div>
          <span className="ml-auto text-xs text-[var(--text-muted)]">letzte {data.production_orders.length}</span>
        </div>
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Nr.</TableHead>
              <TableHead className="overline">Datum</TableHead>
              <TableHead className="overline text-right">Menge</TableHead>
              <TableHead className="overline">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.production_orders.map((p) => (
              <TableRow key={p.id} data-testid={`detail-prod-${p.number || p.id}`}>
                <TableCell className="font-mono font-bold text-xs">
                  <Link to={`/produktion?id=${p.id}`} className="hover:underline text-[var(--pop-pink)]">
                    {p.number || p.id?.slice(-6)}
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-xs">{(p.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right font-mono">{p.quantity ?? "—"}</TableCell>
                <TableCell>
                  <span className="pop-pill text-[10px] font-bold bg-blue-100 text-blue-800">{p.status || "—"}</span>
                </TableCell>
              </TableRow>
            ))}
            {data.production_orders.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-[var(--text-muted)]">Keine Produktionsaufträge.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Chronologische Bewegungshistorie */}
      <Card className="p-0 overflow-hidden rounded-2xl">
        <div className="p-3 border-b flex items-center gap-2">
          <History size={16} className="text-[var(--pop-black)]" />
          <div className="font-display font-bold text-lg">Bewegungshistorie</div>
          <span className="ml-auto text-xs text-[var(--text-muted)]">letzte {(data.history || []).length}</span>
        </div>
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Datum</TableHead>
              <TableHead className="overline">Typ</TableHead>
              <TableHead className="overline">Referenz</TableHead>
              <TableHead className="overline text-right">Menge</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.history || []).map((h) => {
              const qty = Number(h.quantity || 0);
              const isIn = qty > 0;
              const Icon = h.kind === "receive" ? PackagePlus :
                            h.kind === "sale" ? TrendingDown :
                            h.kind === "production" ? Factory :
                            h.kind === "move" ? Move : TrendingUp;
              const color = isIn ? "text-green-700" : "text-red-700";
              return (
                <TableRow key={h.id} data-testid={`detail-history-${h.id}`}>
                  <TableCell className="font-mono text-xs">{(h.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold">
                      <Icon size={12} className={color} /> {h.label}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{h.reference || "—"}</TableCell>
                  <TableCell className={`text-right font-mono font-bold ${color}`}>
                    {isIn ? "+" : ""}{qty.toLocaleString("de-DE")} <span className="text-xs text-[var(--text-muted)]">{data.unit}</span>
                  </TableCell>
                </TableRow>
              );
            })}
            {(!data.history || data.history.length === 0) && (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-[var(--text-muted)]">Keine Bewegungen erfasst.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function StatBox({ label, value, sub, highlight, tone }) {
  const bg = tone === "green" ? "bg-green-50 border-green-300"
           : tone === "red" ? "bg-red-50 border-red-300"
           : tone === "yellow" ? "bg-yellow-50 border-yellow-300"
           : highlight ? "bg-[var(--pop-pink)]/5 border-[var(--pop-pink)]/30" : "";
  return (
    <div className={`rounded-xl border p-3 ${bg}`}>
      <div className="overline text-[var(--text-muted)] text-[10px]">{label}</div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
      {sub && <div className="text-[10px] text-[var(--text-muted)]">{sub}</div>}
    </div>
  );
}
